<!-- src/views/admin/AdminTaskListView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & INTEGRATED COMMAND ACTIONS -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 animate-fadeIn relative overflow-hidden">
          <div class="absolute top-0 right-0 p-12 opacity-5"><ClipboardDocumentCheckIcon class="h-32 w-32 text-white" /></div>
          
          <div class="relative z-10">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8">
              <div>
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Task Command Centre</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4">Action Registry • Operational Oversight & Compliance</p>
              </div>
              
              <div class="flex flex-wrap items-center gap-3">
                <!-- 🛡️ NAVIGATION STANDARDISED: Updated button text to 'Return' and matched padding -->
                <router-link v-slot="{ href, navigate }" to="/client-admin" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 no-underline">
                    <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
                  </a>
                </router-link>
              </div>
            </div>

            <!-- INTEGRATED FILTER ROW -->
            <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white/5 p-6 rounded-[32px] border border-white/10">
              <div class="flex flex-wrap gap-4 items-center">
                <div class="flex flex-col gap-1.5 min-w-[280px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Registry</span>
                  <input v-model="searchQuery" type="text" placeholder="Task title or personnel..." class="filter-input-dark" />
                </div>

                <div class="flex flex-col gap-1.5 min-w-[180px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Status Filter</span>
                  <select v-model="filterStatus" class="filter-select-dark">
                    <option value="All">All Actions</option>
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                <div class="flex flex-col gap-1.5 min-w-[180px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Priority Tier</span>
                  <select v-model="filterPriority" class="filter-select-dark">
                    <option value="All">All Priorities</option>
                    <option value="High">High Priority</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>

              <!-- 🛡️ RE-STYLED EXPORT BUTTON: Styled exactly like the dark glass Training/Documents export button -->
              <button 
                @click="exportToExcel"
                :disabled="exporting || filteredTasks.length === 0"
                class="px-6 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 h-[46px] disabled:opacity-50 shadow-lg cursor-pointer border-none shrink-0"
              >
                <template v-if="exporting">
                  <div class="animate-spin h-3 w-3 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                  <span>Syncing...</span>
                </template>
                <template v-else>
                  <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Current List</span>
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- 🛡️ 2. SENTINEL INTELLIGENCE & INTEGRATED METRICS CARD -->
        <div class="mb-12 animate-fadeIn text-left">
          <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-2xl overflow-hidden relative">
            <div class="bg-[#0A1630] px-10 py-6 border-b-4 border-[#20FCF1] flex flex-col md:flex-row justify-between items-center gap-6">
               <div class="flex items-center gap-4">
                  <div class="bg-[#20FCF1]/10 p-3 rounded-2xl">
                    <SparklesIcon class="h-6 w-6 text-[#20FCF1]" />
                  </div>
                  <div>
                    <h2 class="text-xl font-black text-white uppercase tracking-tight leading-none">Sentinel Intelligence</h2>
                    <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mt-2">Active Task Control Synthesis • Deterministic Audit Logic</p>
                  </div>
               </div>
               
               <button 
                 @click="triggerSynthesis" 
                 :disabled="isAiLoading"
                 class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg"
               >
                 <span v-if="isAiLoading" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                 <SignalIcon v-else class="h-4 w-4" />
                 <span>{{ isAiLoading ? 'Synthesising...' : 'Synchronise Intelligence' }}</span>
               </button>
            </div>

            <div class="p-8 md:p-12 text-left">
               <!-- 📊 INTEGRATED METRICS ROW inside Sentinel Intelligence Card -->
               <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b border-slate-100 pb-8 text-left animate-fadeIn">
                 <div v-for="stat in taskStats" :key="stat.label" class="flex items-center gap-4 text-left">
                   <div class="relative w-12 h-12 flex items-center justify-center text-center">
                     <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                         <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="stat.color" fill-opacity="0.1" :stroke="stat.color" stroke-width="1.5"/>
                     </svg>
                     <component :is="stat.icon" class="w-5 h-5" :style="{ color: stat.color }" />
                   </div>
                   <div class="text-left">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">{{ stat.label }}</p>
                     <h4 class="text-xl font-black text-[#0A1630] leading-none text-left">{{ stat.value }}</h4>
                   </div>
                 </div>
               </div>

               <!-- Initial State -->
               <div v-if="!aiReport && !isAiLoading" class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="bg-slate-50 p-6 rounded-full mb-6">
                    <CpuChipIcon class="h-10 w-10 text-slate-200" />
                  </div>
                  <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-test">Awaiting Initialisation</h3>
                  <p class="text-xs text-slate-400 font-bold mt-2 max-w-sm">Trigger a synchronisation to audit current task lists, overdue action items, and personnel allocations.</p>
               </div>

               <!-- 🛡️ LOCAL SYNTHESIS CONTENT -->
               <div v-else-if="aiReport" class="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div class="lg:col-span-2 space-y-8">
                     <div class="border-l-4 border-[#20FCF1] pl-6">
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Executive Summary</h4>
                        <p class="text-sm font-medium text-slate-600 leading-relaxed italic">
                          "{{ aiReport.summary }}"
                        </p>
                     </div>

                     <div>
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Priority Interventions</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div v-for="(action, idx) in aiReport.actions" :key="idx" class="bg-slate-50 p-5 rounded-3xl border border-slate-100 flex flex-col justify-between">
                              <div>
                                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest block mb-2">{{ action.type }}</span>
                                <p class="text-xs font-black text-[#0A1630] leading-tight mb-3 uppercase tracking-tight">{{ action.instruction }}</p>
                              </div>
                              <p class="text-[10px] text-slate-500 font-medium leading-relaxed">{{ action.reason }}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="bg-[#0A1630]/5 p-8 rounded-[32px] border border-slate-100 h-fit">
                    <h4 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b border-[#0A1630]/10 pb-4">Task Hotspots</h4>
                    <div class="space-y-6">
                       <div v-for="(trend, idx) in aiReport.trends" :key="idx" class="flex items-start gap-4">
                          <div :class="trend.isCritical ? 'bg-rose-500' : 'bg-[#20FCF1]'" class="h-2 w-2 rounded-full shrink-0 mt-1.5 shadow-sm"></div>
                          <div>
                            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ trend.label }}</p>
                            <p class="text-[11px] text-slate-500 font-medium mt-1 leading-tight">{{ trend.value }}</p>
                          </div>
                       </div>
                    </div>
                    <div class="mt-8 pt-6 border-t border-[#0A1630]/10 text-[9px] font-black text-slate-400 uppercase">
                      Analysis Depth: Complete Task Profile
                    </div>
                  </div>
               </div>

               <!-- Loading State -->
               <div v-if="isAiLoading" class="flex flex-col items-center justify-center py-12">
                  <div class="animate-spin h-12 w-12 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
                  <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Aggregating Cross-Task Data & Synthesising Findings...</p>
               </div>
            </div>
          </div>
        </div>

        <!-- 3. DATA DISPLAY -->
        <div v-if="loading" class="text-center py-24">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Hydrating Task Registry...</p>
        </div>

        <div v-else-if="paginatedTasks.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 shadow-inner animate-fadeIn">
           <MagnifyingGlassIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Registry Neutral</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">No matching actions identified.</p>
        </div>

        <div v-else class="space-y-4 animate-fadeIn">
          <div 
            v-for="task in paginatedTasks" 
            :key="task.id"
            class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group"
          >
            <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
              
              <!-- TASK IDENTITY -->
              <div class="flex items-center gap-6 w-full xl:w-[35%] shrink-0">
                <div class="relative h-16 w-16 shrink-0 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 shadow-inner group-hover:bg-[#0A1630] transition-colors duration-500">
                  <ClipboardDocumentCheckIcon class="h-8 w-8 text-slate-300 group-hover:text-[#FD5E00]" />
                </div>
                <div class="min-w-0">
                  <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight leading-tight truncate mb-2">
                    {{ task.title }}
                  </h3>
                  <div class="flex items-center gap-3">
                    <span :class="getPriorityClass(task.priority)" class="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md">
                        {{ task.priority }} Priority
                    </span>
                    <span class="text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">Due: {{ formatDate(task.dueDate) }}</span>
                  </div>
                </div>
              </div>

              <!-- ASSIGNMENT INFO -->
              <div class="flex-grow px-8 border-l border-slate-50 min-w-0">
                <div class="flex items-center gap-4">
                   <div class="text-left">
                      <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1">Assigned Personnel</p>
                      <p class="text-sm font-black text-[#0A1630] uppercase leading-none">{{ task.targetUserName || 'Unassigned' }}</p>
                   </div>
                   <div class="h-8 w-px bg-slate-100 mx-2"></div>
                   <div class="text-left">
                      <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1">Source Agent</p>
                      <p class="text-[10px] font-bold text-slate-500 uppercase leading-none">{{ task.assignedByName || 'System' }}</p>
                   </div>
                </div>
                <p v-if="isOverdue(task)" class="text-[9px] font-black text-rose-600 uppercase mt-3 animate-pulse">Critical: Task Overdue</p>
              </div>

              <!-- ACTIONS -->
              <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6">
                 <div class="text-right shrink-0">
                    <span
                      :class="task.status === 'Completed' ? 'bg-[#EBFDF8] text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'"
                      class="px-5 py-2 inline-flex text-[9px] font-black rounded-full border uppercase tracking-widest shadow-sm"
                    >
                      {{ task.status || 'PENDING' }}
                    </span>
                 </div>

                 <button 
                  v-if="hasTaskClearance"
                  @click="deleteTask(task.id)"
                  class="p-4 bg-slate-50 text-slate-400 rounded-2xl hover:bg-rose-500 hover:text-white transition-all shadow-sm active:scale-95 cursor-pointer border-none"
                >
                  <TrashIcon class="h-5 w-5" />
                </button>
              </div>
            </div>
            <div v-if="task.description" class="mt-4 pt-4 border-t border-slate-50">
               <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wide italic">"{{ task.description }}"</p>
            </div>
          </div>
        </div>

        <!-- Symmetrical Pagination Controls (Forced h-11 heights) -->
        <div v-if="totalPages > 1 || itemsPerPage !== 25" class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12 no-print">
          <div class="flex items-center gap-4">
            <button 
              @click="prevPage" 
              :disabled="currentPage === 1"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              &larr; Prev
            </button>
            <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
              Page {{ currentPage }} of {{ totalPages || 1 }}
            </span>
            <button 
              @click="nextPage" 
              :disabled="currentPage === totalPages || totalPages === 0"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              Next &rarr;
            </button>
          </div>
          
          <!-- Symmetrical Items Per Page Selector (Forced h-11 height) -->
          <div class="flex items-center gap-3">
            <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
            <select 
              v-model="itemsPerPage" 
              class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
              style="background-image: url('data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2320FCF1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem;"
            >
              <option :value="25">25 Rows</option>
              <option :value="50">50 Rows</option>
              <option :value="100">100 Rows</option>
            </select>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { db, auth } from '@/firebaseConfig';
import { collection, query, where, doc, getDoc, orderBy, onSnapshot, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
import { 
  ClipboardDocumentCheckIcon, ArrowUturnLeftIcon, 
  TrashIcon, TableCellsIcon, MagnifyingGlassIcon,
  CheckBadgeIcon, ExclamationTriangleIcon, ClockIcon,
  SparklesIcon, CpuChipIcon, SignalIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const functions = getFunctions(auth.app, 'europe-west2');

const tasks = ref([]);
const loading = ref(true);
const exporting = ref(false);

// PAGINATION STATE (Defaulting to 25 rows with forced h-11 logic)
const currentPage = ref(1);
const itemsPerPage = ref(25);

// 🛡️ SYNTHESIS STATE REGISTRY
const aiReport = ref(null);
const isAiLoading = ref(false);

// 🛡️ STICKY SELECTIONS & FILTERS: Read from localStorage dynamically
const searchQuery = ref(localStorage.getItem('tasks_sticky_search') || '');
const filterStatus = ref(localStorage.getItem('tasks_sticky_status') || 'All');
const filterPriority = ref(localStorage.getItem('tasks_sticky_priority') || 'All');

// 🛡️ STICKY WATCHERS: Write filter selections to localStorage dynamically
watch(searchQuery, (val) => localStorage.setItem('tasks_sticky_search', val));
watch(filterStatus, (val) => localStorage.setItem('tasks_sticky_status', val));
watch(filterPriority, (val) => localStorage.setItem('tasks_sticky_priority', val));

// Reset page back to 1 on filter updates
watch([searchQuery, filterStatus, filterPriority, itemsPerPage], () => {
  currentPage.value = 1;
});

const isOverdue = (task) => {
    if (!task.dueDate || task.status === 'Completed') return false;
    return new Date(task.dueDate) < new Date();
};

const taskStats = computed(() => {
  return [
    { label: 'Total Registry', value: tasks.value.length, color: '#6366F1', icon: ClipboardDocumentCheckIcon },
    { label: 'Awaiting Action', value: tasks.value.filter(t => t.status !== 'Completed').length, color: '#FBBF24', icon: ClockIcon },
    { label: 'Completed', value: tasks.value.filter(t => t.status === 'Completed').length, color: '#20FCF1', icon: CheckBadgeIcon },
    { label: 'Overdue Critical', value: tasks.value.filter(t => isOverdue(t)).length, color: '#EF4444', icon: ExclamationTriangleIcon },
  ];
});

const filteredTasks = computed(() => {
    return tasks.value.filter(t => {
        const matchStatus = filterStatus.value === 'All' || t.status === filterStatus.value;
        const matchPriority = filterPriority.value === 'All' || t.priority === filterPriority.value;
        const matchSearch = !searchQuery.value || 
            (t.title || '').toLowerCase().includes(searchQuery.value.toLowerCase()) || 
            (t.targetUserName || '').toLowerCase().includes(searchQuery.value.toLowerCase());
        
        return matchStatus && matchPriority && matchSearch;
    });
});

// PAGINATION MATH
const totalPages = computed(() => {
  return Math.ceil(filteredTasks.value.length / itemsPerPage.value);
});

const paginatedTasks = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredTasks.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

// 🛡️ RECONCILED IDENTITY RESOLUTION: Secure computed client tracking prevents Super Admin context crashes
const currentClientId = computed(() => userStore.clientId || userStore.userProfile?.clientId);

// 🛡️ UNCONDITIONAL PLATFORM AUTHORITY OVERRIDE: Simplifies permissions so all Command Access Admins can manage everything
const hasTaskClearance = computed(() => {
  return userStore.role === 'super-admin' || 
         userStore.isAdmin || 
         userStore.hasAdminAccess ||
         userStore.permissions?.canViewTasks === true ||
         userStore.permissions?.canManageTasks === true;
});

// 🛡️ DETERMINISTIC SYNTHESIS ENGINE
const triggerSynthesis = async () => {
    isAiLoading.value = true;
    const cid = currentClientId.value;
    if (!cid) { alert("Identity context missing."); isAiLoading.value = false; return; }
    
    await new Promise(r => setTimeout(r, 1500));

    try {
        const total = tasks.value.length;
        const pendingCount = tasks.value.filter(t => t.status !== 'Completed').length;
        const completedCount = tasks.value.filter(t => t.status === 'Completed').length;
        const overdueCount = tasks.value.filter(t => isOverdue(t)).length;

        const completionRate = total > 0 ? Math.round((completedCount / total) * 100) : 100;

        const report = {
            summary: `Task governance and compliance audit monitors ${total} total operations logs. Current operational workflow maintains a ${completionRate}% task resolution rate. Identified ${overdueCount} critical overdue actions requiring immediate administrative escalation, with ${pendingCount} standard tasks currently pending completion.`,
            trends: [
                { label: 'Escalation Index', value: `${overdueCount} Overdue critical logs`, isCritical: overdueCount > 0 },
                { label: 'Workforce Velocity', value: `${completedCount} Completed tasks`, isCritical: false },
                { label: 'Pending Queue', value: `${pendingCount} Tasks outstanding`, isCritical: pendingCount > 3 }
            ],
            actions: [
                { type: 'Escalation Triage', instruction: `Address ${overdueCount} overdue actions`, reason: 'Overdue tasks compromise operations alignment and compliance indexes.' },
                { type: 'SLA Review', instruction: `Audit outstanding ${pendingCount} logs`, reason: 'Regular folder triages ensure systematic workforce task completions.' }
            ],
            createdAt: new Date().toISOString()
        };

        const reportRef = doc(db, 'clients', cid, 'task_intelligence', 'latest');
        await setDoc(reportRef, { ...report, clientId: cid }, { merge: true });
        aiReport.value = report;

    } catch (e) {
        console.error("Synthesis Protocol Failure:", e);
        alert("Registry Handshake Failure: Insufficient authorisation to save intelligence log.");
    } finally {
        isAiLoading.value = false;
    }
};

const fetchLatestAiReport = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'clients', cid, 'task_intelligence', 'latest'));
    if (docSnap.exists()) {
      aiReport.value = docSnap.data();
    }
  } catch (err) {
    console.warn("Task Intelligence Registry Neutral.");
  }
};

const setupListeners = async (clientId) => {
    loading.value = true;
    await fetchLatestAiReport(clientId);

    const q = query(collection(db, 'user_actions'), where('clientId', '==', clientId), orderBy('createdAt', 'desc'));
    
    onSnapshot(q, async (snap) => {
        const tasksData = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        const userCache = {};
        for (const task of tasksData) {
            if (!userCache[task.targetUserId]) {
                const userSnap = await getDoc(doc(db, 'userProfiles', task.targetUserId));
                userCache[task.targetUserId] = userSnap.exists() ? `${userSnap.data().firstName} ${userSnap.data().lastName}` : 'Personnel Not Found';
            }
            task.targetUserName = userCache[task.targetUserId];
        }
        tasks.value = tasksData;
        loading.value = false;
    });
};

const deleteTask = async (taskId) => {
    if (!confirm("Authorise permanent removal of this task record? This action is immutable.")) return;
    try {
        await httpsCallable(functions, 'dismissAction')({ actionId: taskId });
    } catch (err) { alert("Registry Deletion Failed."); }
};

const getPriorityClass = (priority) => {
  switch(priority) {
    case 'High': return 'bg-rose-50 text-rose-600 border border-rose-100';
    case 'Medium': return 'bg-amber-50 text-amber-600 border border-amber-100';
    default: return 'bg-blue-50 text-blue-600 border border-blue-100';
  }
};

const formatDate = (val) => {
  if (!val) return 'N/A';
  const d = val.toDate ? val.toDate() : new Date(val);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const exportToExcel = () => {
  exporting.value = true;
  try {
    const dataToExport = filteredTasks.value.map(t => ({
      'Task Title': t.title,
      'Personnel': t.targetUserName,
      'Priority': t.priority,
      'Status': t.status,
      'Due Date': formatDate(t.dueDate),
      'Assigned By': t.assignedByName || 'System'
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Task_Registry");
    XLSX.writeFile(wb, `Sentinel_Task_Audit_Export.xlsx`);
  } catch (err) { alert(err.message); } finally { exporting.value = false; }
};

// 🛡️ RECONCILED SYNC: Watches computed client ID reactively to resolve race condition crashes
watch(currentClientId, (newId) => {
  if (newId && userStore.isAuthReady) {
    setupListeners(newId);
  }
}, { immediate: true });
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-2xl px-5 py-3 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500; }
.filter-select-dark {
  @apply w-full bg-[#1E293B] border border-white/10 rounded-2xl px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1rem;
}
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>